/* saveWithRetry — Robust axios wrapper for save-style writes.
 *
 * Why this exists: production users were seeing intermittent "Failed to
 * save" toasts caused by transient network blips (Cloudflare 524/520, ECS
 * pod restarts, etc.). The data was always fine on the next click. This
 * helper:
 *   1. Retries the request with exponential backoff (3 attempts).
 *   2. Only retries on transient failures that are safe to retry —
 *      network errors + upstream 502/503/504/520-524/529. Ambiguous
 *      statuses (500, 408, 429, 525-527) are NOT retried because a slow
 *      write can succeed on the server while the browser sees a 500, and
 *      retrying THAT is what caused the 6-copy supplier-journal bug.
 *      Callers that MUST retry every write should pair this with an
 *      idempotency key on the backend.
 *   3. Surfaces the actual backend error to the caller's toast instead
 *      of a generic "Failed to save" string.
 *
 * Usage:
 *   import { saveWithRetry, errorMessage } from "@/lib/saveWithRetry";
 *   try {
 *     await saveWithRetry(() => axios.put(url, payload));
 *     toast.success("Saved");
 *   } catch (e) {
 *     toast.error(errorMessage(e, "Save failed"));
 *   }
 */
import axios from "axios";

// Only truly-transient upstream errors — every one of these is emitted BEFORE
// the request reaches the app so retrying is guaranteed safe.
// Deliberately EXCLUDES 500 (application error, may have succeeded server-side)
// and 408/429 (which can also duplicate on non-idempotent endpoints).
const TRANSIENT_STATUSES = new Set([502, 503, 504, 520, 521, 522, 523, 524, 529]);

const isTransient = (e) => {
  if (!e) return false;
  if (axios.isCancel?.(e)) return false;
  if (e.code === "ECONNABORTED" || e.code === "ECONNRESET" || e.code === "ETIMEDOUT" || e.code === "ERR_NETWORK") return true;
  const status = e?.response?.status ?? 0;
  return TRANSIENT_STATUSES.has(status);
};

const delay = (ms) => new Promise((r) => setTimeout(r, ms));

export async function saveWithRetry(requestFn, { retries = 3, baseDelayMs = 500 } = {}) {
  let lastErr;
  for (let attempt = 0; attempt < retries; attempt++) {
    try {
      return await requestFn();
    } catch (e) {
      lastErr = e;
      if (!isTransient(e) || attempt === retries - 1) throw e;
      // Exponential backoff: 0.5s, 1.5s, 4s
      await delay(baseDelayMs * Math.pow(3, attempt));
    }
  }
  throw lastErr;
}

export function errorMessage(e, fallback = "Save failed") {
  if (!e) return fallback;
  const detail = e?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
  const status = e?.response?.status;
  if (status && status >= 500) {
    return `${fallback} — server returned ${status}. Please try again.`;
  }
  if (e?.code === "ERR_NETWORK") {
    return `${fallback} — network is unreachable. Check your connection and retry.`;
  }
  return e?.message || fallback;
}
